import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { UserPlus, Calendar as CalendarIcon, Clock, AlertCircle, Check, ChevronLeft, ChevronRight, User } from 'lucide-react';
import Header from '../components/Layout/Header';
import Modal from '../components/Modal';
import { doctors, patients } from '../data/mockData';

// Horarios de trabajo por médico (en producción vendría de una API)
const doctorSchedules = {
  1: { start: '09:00', end: '18:00', interval: 30 },
  2: { start: '08:00', end: '17:00', interval: 30 },
  3: { start: '10:00', end: '19:00', interval: 30 },
  4: { start: '09:00', end: '16:00', interval: 30 },
};

// Citas ocupadas por médico y fecha (en producción vendría de una API)
const occupiedSlots = {
  1: {
    '2025-12-10': ['09:00', '09:30', '11:00', '14:00', '14:30'],
    '2025-12-11': ['10:00', '10:30', '15:00'],
  },
  2: {
    '2025-12-10': ['08:00', '08:30', '12:00'],
    '2025-12-11': ['09:00', '14:00', '14:30', '15:00'],
  },
  3: {
    '2025-12-10': ['10:00', '13:00', '16:00'],
  },
  4: {
    '2025-12-10': ['09:00', '11:00', '11:30'],
  },
};

// Servicios por médico (en producción vendría de una API)
const doctorServices = {
  1: [
    { id: 1, name: 'Electrocardiograma Pediátrico', price: 350, duration: 30 },
    { id: 2, name: 'Ecocardiograma Fetal', price: 800, duration: 45 },
    { id: 3, name: 'Consulta de Arritmias', price: 500, duration: 30 },
    { id: 4, name: 'Seguimiento Post-operatorio', price: 400, duration: 30 },
    { id: 5, name: 'Evaluación Pre-deportiva', price: 300, duration: 20 },
  ],
  2: [
    { id: 1, name: 'Consulta General', price: 300, duration: 30 },
    { id: 2, name: 'Vacunación', price: 150, duration: 15 },
    { id: 3, name: 'Control de Niño Sano', price: 350, duration: 40 },
  ],
  3: [
    { id: 1, name: 'Consulta Dermatológica', price: 400, duration: 30 },
    { id: 2, name: 'Tratamiento de Acné', price: 600, duration: 45 },
    { id: 3, name: 'Cirugía Menor', price: 1200, duration: 60 },
  ],
  4: [
    { id: 1, name: 'Examen Visual', price: 250, duration: 20 },
    { id: 2, name: 'Cirugía Láser', price: 5000, duration: 90 },
    { id: 3, name: 'Tratamiento de Cataratas', price: 8000, duration: 120 },
  ],
};

const PageContainer = styled.div`
  flex: 1;
  background: ${({ theme }) => theme.colors.background};
  padding-bottom: 80px;
  overflow-y: auto;
`;

const Content = styled.div`
  padding: 20px;
`;

const Form = styled.form`
  background: ${({ theme }) => theme.colors.white};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: 24px;
  box-shadow: ${({ theme }) => theme.shadows.sm};
`;

const FormSection = styled.div`
  margin-bottom: 24px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.h3`
  font-size: ${({ theme }) => theme.fontSizes.md};
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  color: ${({ theme }) => theme.colors.text};
  margin: 0 0 16px 0;
`;

const FormField = styled.div`
  margin-bottom: 16px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const Label = styled.label`
  display: block;
  font-size: ${({ theme }) => theme.fontSizes.sm};
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: 8px;
`;

const InputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 16px;
  padding-left: ${({ $hasIcon }) => $hasIcon ? '44px' : '16px'};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.fontSizes.md};
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors.textSecondary};
  }
`;

const InputIcon = styled.div`
  position: absolute;
  left: 14px;
  display: flex;
  align-items: center;
  color: ${({ theme }) => theme.colors.textSecondary};

  svg {
    width: 20px;
    height: 20px;
  }
`;

const IconButton = styled.button`
  position: absolute;
  right: 12px;
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.primary};
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 20px;
    height: 20px;
  }

  &:hover {
    color: ${({ theme }) => theme.colors.primaryDark};
  }
`;

const AutocompleteWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const SuggestionsList = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-top: none;
  border-radius: 0 0 ${({ theme }) => theme.borderRadius.md} ${({ theme }) => theme.borderRadius.md};
  max-height: 200px;
  overflow-y: auto;
  z-index: 100;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
`;

const SuggestionItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: ${({ theme }) => theme.colors.gray};
  }
`;

const SuggestionAvatar = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.primary}20;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  flex-shrink: 0;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  svg {
    width: 18px;
    height: 18px;
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const SuggestionInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const SuggestionName = styled.div`
  font-size: 14px;
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  color: ${({ theme }) => theme.colors.text};
`;

const SuggestionDetail = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const Select = styled.select`
  width: 100%;
  padding: 12px 16px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.fontSizes.md};
  background: ${({ theme }) => theme.colors.white};
  cursor: pointer;
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const TimeSelect = styled.select`
  width: 100%;
  padding: 12px 16px;
  padding-left: 44px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.fontSizes.md};
  background: ${({ theme }) => theme.colors.white};
  cursor: pointer;
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }

  &:disabled {
    background: ${({ theme }) => theme.colors.gray};
    cursor: not-allowed;
  }

  option {
    padding: 8px;
  }

  option:disabled {
    color: #999;
    background: #f5f5f5;
  }
`;

const TimeSlotInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 8px;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textSecondary};

  svg {
    width: 14px;
    height: 14px;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 12px 16px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.fontSizes.md};
  min-height: 100px;
  resize: vertical;
  font-family: ${({ theme }) => theme.fonts.primary};
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors.textSecondary};
  }
`;

const SubmitButton = styled.button`
  width: 100%;
  padding: 14px;
  background: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.white};
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.fontSizes.md};
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 24px;

  &:hover {
    background: ${({ theme }) => theme.colors.primaryDark};
  }

  &:active {
    transform: scale(0.98);
  }

  &:disabled {
    background: ${({ theme }) => theme.colors.border};
    cursor: not-allowed;
  }
`;

const ServicesGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ServiceCard = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  background: ${({ $selected, theme }) => $selected ? `${theme.colors.primary}10` : theme.colors.background};
  border: 2px solid ${({ $selected, theme }) => $selected ? theme.colors.primary : theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const ServiceCheckbox = styled.div`
  width: 22px;
  height: 22px;
  border-radius: 6px;
  border: 2px solid ${({ $checked, theme }) => $checked ? theme.colors.primary : theme.colors.border};
  background: ${({ $checked, theme }) => $checked ? theme.colors.primary : 'transparent'};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.2s ease;

  svg {
    width: 14px;
    height: 14px;
    color: white;
  }
`;

const ServiceInfo = styled.div`
  flex: 1;
`;

const ServiceName = styled.div`
  font-size: ${({ theme }) => theme.fontSizes.md};
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: 4px;
`;

const ServiceDetails = styled.div`
  display: flex;
  gap: 16px;
  font-size: ${({ theme }) => theme.fontSizes.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const ServicePrice = styled.span`
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  color: ${({ theme }) => theme.colors.primary};
`;

const SelectedServicesInfo = styled.div`
  margin-top: 12px;
  padding: 12px 16px;
  background: ${({ theme }) => theme.colors.primary}10;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const TotalLabel = styled.span`
  font-size: ${({ theme }) => theme.fontSizes.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const TotalValue = styled.span`
  font-size: ${({ theme }) => theme.fontSizes.md};
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  color: ${({ theme }) => theme.colors.text};
`;

// Styled components para el calendario
const CalendarContainer = styled.div`
  background: ${({ theme }) => theme.colors.background};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: 16px;
  border: 1px solid ${({ theme }) => theme.colors.border};
`;

const CalendarHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

const MonthYear = styled.h4`
  font-size: ${({ theme }) => theme.fontSizes.md};
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  color: ${({ theme }) => theme.colors.text};
  margin: 0;
`;

const NavButtons = styled.div`
  display: flex;
  gap: 8px;
`;

const NavButton = styled.button`
  background: none;
  border: none;
  padding: 6px;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.textSecondary};
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  transition: all 0.2s ease;

  svg {
    width: 18px;
    height: 18px;
  }

  &:hover {
    background: ${({ theme }) => theme.colors.gray};
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const WeekDays = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 4px;
  margin-bottom: 8px;
`;

const WeekDay = styled.div`
  text-align: center;
  font-size: 12px;
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  color: ${({ theme }) => theme.colors.textSecondary};
  padding: 6px 0;
`;

const DaysGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 4px;
`;

const DayCell = styled.button`
  width: 36px;
  height: 36px;
  border: none;
  background: ${({ $isToday, $isSelected, theme }) => 
    $isSelected ? theme.colors.primary : 
    $isToday ? `${theme.colors.info}30` : 
    'transparent'};
  color: ${({ $isToday, $isSelected, $isOtherMonth, $isPast, theme }) => 
    $isSelected ? theme.colors.white :
    $isOtherMonth || $isPast ? '#D0D0D0' :
    $isToday ? theme.colors.primary :
    theme.colors.text};
  border-radius: 50%;
  font-size: 13px;
  font-weight: ${({ $isSelected, theme }) => 
    $isSelected ? theme.fontWeights.semibold : theme.fontWeights.normal};
  cursor: ${({ $isPast, $isOtherMonth }) => $isPast || $isOtherMonth ? 'not-allowed' : 'pointer'};
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  justify-self: center;

  &:hover {
    background: ${({ $isSelected, $isPast, $isOtherMonth, theme }) => 
      $isPast || $isOtherMonth ? 'transparent' :
      $isSelected ? theme.colors.primaryDark : theme.colors.gray};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.3;
  }
`;

const ReservarCita = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    patient: '',
    date: '',
    time: '',
    services: [],
    doctor: '',
    duration: '',
    notes: ''
  });
  const [timeSlots, setTimeSlots] = useState([]);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  
  // Estado para sugerencias de pacientes
  const [patientSearch, setPatientSearch] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const suggestionsRef = useRef(null);
  
  // Filtrar pacientes según búsqueda
  const filteredPatients = patients.filter(patient =>
    patient.name.toLowerCase().includes(patientSearch.toLowerCase()) ||
    patient.patientId?.toLowerCase().includes(patientSearch.toLowerCase()) ||
    patient.dni?.toLowerCase().includes(patientSearch.toLowerCase())
  );

  // Cerrar sugerencias al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handlePatientSearchChange = (e) => {
    const value = e.target.value;
    setPatientSearch(value);
    setShowSuggestions(value.length > 0);
    setSelectedPatient(null);
    setFormData(prev => ({ ...prev, patient: value }));
  };

  const handleSelectPatient = (patient) => {
    setSelectedPatient(patient);
    setPatientSearch(patient.name);
    setFormData(prev => ({ ...prev, patient: patient.name }));
    setShowSuggestions(false);
  };
  
  // Estado para el calendario
  const today = new Date();
  const [currentMonth, setCurrentMonth] = useState(today.getMonth());
  const [currentYear, setCurrentYear] = useState(today.getFullYear());

  // Nombres de meses y días
  const monthNames = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
  const weekDays = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];

  // Generar días del calendario
  const generateCalendarDays = () => {
    const firstDay = new Date(currentYear, currentMonth, 1);
    const lastDay = new Date(currentYear, currentMonth + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay();
    
    const days = [];
    
    // Días del mes anterior
    const prevMonthLastDay = new Date(currentYear, currentMonth, 0).getDate();
    for (let i = startingDay - 1; i >= 0; i--) {
      days.push({
        day: prevMonthLastDay - i,
        isOtherMonth: true,
        date: new Date(currentYear, currentMonth - 1, prevMonthLastDay - i)
      });
    }
    
    // Días del mes actual
    for (let i = 1; i <= daysInMonth; i++) {
      const date = new Date(currentYear, currentMonth, i);
      days.push({
        day: i,
        isOtherMonth: false,
        date: date
      });
    }
    
    // Días del siguiente mes
    const remainingDays = 42 - days.length;
    for (let i = 1; i <= remainingDays; i++) {
      days.push({
        day: i,
        isOtherMonth: true,
        date: new Date(currentYear, currentMonth + 1, i)
      });
    }
    
    return days;
  };

  const calendarDays = generateCalendarDays();

  const handlePrevMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
  };

  const handleNextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
  };

  const handleDateSelect = (dayInfo) => {
    if (dayInfo.isOtherMonth) return;
    
    const selectedDate = dayInfo.date;
    const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    
    if (selectedDate < todayStart) return; // No permitir fechas pasadas
    
    const year = selectedDate.getFullYear();
    const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
    const day = String(selectedDate.getDate()).padStart(2, '0');
    const dateString = `${year}-${month}-${day}`;
    
    setFormData(prev => ({ ...prev, date: dateString, time: '' }));
  };

  const isDateSelected = (dayInfo) => {
    if (!formData.date) return false;
    const selectedDate = new Date(formData.date + 'T00:00:00');
    return dayInfo.date.getDate() === selectedDate.getDate() &&
           dayInfo.date.getMonth() === selectedDate.getMonth() &&
           dayInfo.date.getFullYear() === selectedDate.getFullYear();
  };

  const isToday = (dayInfo) => {
    return dayInfo.date.getDate() === today.getDate() &&
           dayInfo.date.getMonth() === today.getMonth() &&
           dayInfo.date.getFullYear() === today.getFullYear();
  };

  const isPastDate = (dayInfo) => {
    const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    return dayInfo.date < todayStart;
  };

  // Si solo hay un médico, seleccionarlo automáticamente
  useEffect(() => {
    if (doctors.length === 1) {
      setFormData(prev => ({
        ...prev,
        doctor: doctors[0].id.toString()
      }));
    }
  }, []);

  // Generar slots de tiempo cuando cambia médico o fecha
  useEffect(() => {
    if (formData.doctor && formData.date) {
      const doctorId = parseInt(formData.doctor);
      const schedule = doctorSchedules[doctorId];
      
      if (schedule) {
        const slots = generateTimeSlots(
          schedule.start, 
          schedule.end, 
          schedule.interval,
          doctorId,
          formData.date
        );
        setTimeSlots(slots);
      }
    } else {
      setTimeSlots([]);
    }
  }, [formData.doctor, formData.date]);

  // Función para generar los slots de tiempo
  const generateTimeSlots = (start, end, interval, doctorId, date) => {
    const slots = [];
    const [startHour, startMin] = start.split(':').map(Number);
    const [endHour, endMin] = end.split(':').map(Number);
    
    let currentHour = startHour;
    let currentMin = startMin;
    
    const occupied = occupiedSlots[doctorId]?.[date] || [];
    
    while (currentHour < endHour || (currentHour === endHour && currentMin < endMin)) {
      const timeStr = `${currentHour.toString().padStart(2, '0')}:${currentMin.toString().padStart(2, '0')}`;
      const isOccupied = occupied.includes(timeStr);
      
      slots.push({
        time: timeStr,
        available: !isOccupied,
        label: isOccupied ? `${timeStr} - Ocupado` : timeStr
      });
      
      // Incrementar tiempo
      currentMin += interval;
      if (currentMin >= 60) {
        currentHour += Math.floor(currentMin / 60);
        currentMin = currentMin % 60;
      }
    }
    
    return slots;
  };

  // Obtener servicios del médico seleccionado
  const selectedDoctorId = formData.doctor ? parseInt(formData.doctor) : null;
  const availableServices = selectedDoctorId ? (doctorServices[selectedDoctorId] || []) : [];

  const handleSubmit = (e) => {
    e.preventDefault();
    // Aquí iría la lógica para guardar la cita
    setShowSuccessModal(true);
  };

  const handleSuccessClose = () => {
    setShowSuccessModal(false);
    navigate('/agenda');
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Si cambia el médico, resetear servicios, duración y hora
    if (name === 'doctor') {
      setFormData(prev => ({
        ...prev,
        [name]: value,
        services: [],
        duration: '',
        time: ''
      }));
    } 
    // Si cambia la fecha, resetear la hora
    else if (name === 'date') {
      setFormData(prev => ({
        ...prev,
        [name]: value,
        time: ''
      }));
    }
    else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  // Manejar selección de servicios
  const handleServiceToggle = (serviceId) => {
    setFormData(prev => {
      const isSelected = prev.services.includes(serviceId);
      let newServices;
      
      if (isSelected) {
        newServices = prev.services.filter(id => id !== serviceId);
      } else {
        newServices = [...prev.services, serviceId];
      }
      
      // Calcular duración total
      const totalDuration = newServices.reduce((total, id) => {
        const service = availableServices.find(s => s.id === id);
        return total + (service ? service.duration : 0);
      }, 0);
      
      return {
        ...prev,
        services: newServices,
        duration: totalDuration > 0 ? totalDuration.toString() : ''
      };
    });
  };

  // Calcular totales
  const selectedServicesData = formData.services.map(id => availableServices.find(s => s.id === id)).filter(Boolean);
  const totalPrice = selectedServicesData.reduce((sum, s) => sum + s.price, 0);
  const totalDuration = selectedServicesData.reduce((sum, s) => sum + s.duration, 0);

  return (
    <PageContainer>
      <Modal
        isOpen={showSuccessModal}
        onClose={handleSuccessClose}
        title="Cita Reservada"
        message="La cita ha sido reservada exitosamente."
        type="success"
        confirmText="Aceptar"
      />

      <Header title="Reservar Cita" showBack />
      
      <Content>
        <Form onSubmit={handleSubmit}>
          <FormSection>
            <SectionTitle>Paciente</SectionTitle>
            <FormField>
              <Label>Paciente {selectedPatient && `- ${selectedPatient.patientId}`}</Label>
              <AutocompleteWrapper ref={suggestionsRef}>
                <InputWrapper>
                  <Input
                    type="text"
                    name="patient"
                    placeholder="Buscar paciente por nombre, ID o DNI..."
                    value={patientSearch}
                    onChange={handlePatientSearchChange}
                    onFocus={() => patientSearch.length > 0 && setShowSuggestions(true)}
                    required
                    autoComplete="off"
                  />
                  <IconButton type="button" onClick={() => navigate('/registro-paciente')}>
                    <UserPlus />
                  </IconButton>
                </InputWrapper>
                {showSuggestions && filteredPatients.length > 0 && (
                  <SuggestionsList>
                    {filteredPatients.map(patient => (
                      <SuggestionItem key={patient.id} onClick={() => handleSelectPatient(patient)}>
                        <SuggestionAvatar>
                          {patient.image ? (
                            <img src={patient.image} alt={patient.name} />
                          ) : (
                            <User />
                          )}
                        </SuggestionAvatar>
                        <SuggestionInfo>
                          <SuggestionName>{patient.name}</SuggestionName>
                          <SuggestionDetail>{patient.patientId} • {patient.type}</SuggestionDetail>
                        </SuggestionInfo>
                      </SuggestionItem>
                    ))}
                  </SuggestionsList>
                )}
                {showSuggestions && patientSearch.length > 0 && filteredPatients.length === 0 && (
                  <SuggestionsList>
                    <SuggestionItem style={{ justifyContent: 'center', color: '#999' }}>
                      No se encontraron pacientes
                    </SuggestionItem>
                  </SuggestionsList>
                )}
              </AutocompleteWrapper>
            </FormField>
          </FormSection>

          <FormSection>
            <SectionTitle>Doctor y Servicio</SectionTitle>
            <FormField>
              <Label>Seleccionar Doctor</Label>
              <Select
                name="doctor"
                value={formData.doctor}
                onChange={handleChange}
                required
              >
                <option value="">Seleccionar Doctor</option>
                {doctors.map(doctor => (
                  <option key={doctor.id} value={doctor.id}>
                    {doctor.name} - {doctor.specialty}
                  </option>
                ))}
              </Select>
            </FormField>
            <FormField>
              <Label>Servicios {formData.services.length > 0 && `(${formData.services.length} seleccionados)`}</Label>
              {!formData.doctor ? (
                <Select disabled>
                  <option value="">Primero seleccione un doctor</option>
                </Select>
              ) : (
                <>
                  <ServicesGrid>
                    {availableServices.map(service => {
                      const isSelected = formData.services.includes(service.id);
                      return (
                        <ServiceCard 
                          key={service.id} 
                          $selected={isSelected}
                          onClick={() => handleServiceToggle(service.id)}
                        >
                          <ServiceCheckbox $checked={isSelected}>
                            {isSelected && <Check />}
                          </ServiceCheckbox>
                          <ServiceInfo>
                            <ServiceName>{service.name}</ServiceName>
                            <ServiceDetails>
                              <span>{service.duration} min</span>
                              <ServicePrice>${service.price}</ServicePrice>
                            </ServiceDetails>
                          </ServiceInfo>
                        </ServiceCard>
                      );
                    })}
                  </ServicesGrid>
                  {formData.services.length > 0 && (
                    <SelectedServicesInfo>
                      <div>
                        <TotalLabel>Duración total: </TotalLabel>
                        <TotalValue>{totalDuration} min</TotalValue>
                      </div>
                      <div>
                        <TotalLabel>Total: </TotalLabel>
                        <TotalValue>${totalPrice}</TotalValue>
                      </div>
                    </SelectedServicesInfo>
                  )}
                </>
              )}
            </FormField>
            <FormField>
              <Label>Duración (minutos)</Label>
              <Input
                type="number"
                name="duration"
                placeholder="Se asigna automáticamente"
                value={formData.duration}
                onChange={handleChange}
                min="15"
                step="15"
                required
                readOnly
              />
            </FormField>
          </FormSection>

          <FormSection>
            <SectionTitle>Fecha y Hora</SectionTitle>
            <FormField>
              <Label>Fecha {formData.date && `- ${new Date(formData.date + 'T00:00:00').toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' })}`}</Label>
              {!formData.doctor ? (
                <CalendarContainer style={{ opacity: 0.5, pointerEvents: 'none' }}>
                  <div style={{ textAlign: 'center', padding: '20px', color: '#999' }}>
                    Primero seleccione un doctor
                  </div>
                </CalendarContainer>
              ) : (
                <CalendarContainer>
                  <CalendarHeader>
                    <NavButton onClick={handlePrevMonth}>
                      <ChevronLeft />
                    </NavButton>
                    <MonthYear>{monthNames[currentMonth]} {currentYear}</MonthYear>
                    <NavButton onClick={handleNextMonth}>
                      <ChevronRight />
                    </NavButton>
                  </CalendarHeader>
                  <WeekDays>
                    {weekDays.map(day => (
                      <WeekDay key={day}>{day}</WeekDay>
                    ))}
                  </WeekDays>
                  <DaysGrid>
                    {calendarDays.map((dayInfo, index) => (
                      <DayCell
                        key={index}
                        type="button"
                        $isToday={isToday(dayInfo)}
                        $isSelected={isDateSelected(dayInfo)}
                        $isOtherMonth={dayInfo.isOtherMonth}
                        $isPast={isPastDate(dayInfo) && !dayInfo.isOtherMonth}
                        onClick={() => handleDateSelect(dayInfo)}
                        disabled={dayInfo.isOtherMonth || isPastDate(dayInfo)}
                      >
                        {dayInfo.day}
                      </DayCell>
                    ))}
                  </DaysGrid>
                </CalendarContainer>
              )}
            </FormField>
            <FormField>
              <Label>Hora</Label>
              <InputWrapper>
                <InputIcon>
                  <Clock />
                </InputIcon>
                <TimeSelect
                  name="time"
                  value={formData.time}
                  onChange={handleChange}
                  required
                  disabled={!formData.doctor || !formData.date}
                >
                  <option value="">
                    {!formData.doctor 
                      ? 'Primero seleccione un doctor' 
                      : !formData.date 
                        ? 'Primero seleccione una fecha'
                        : 'Seleccionar hora'}
                  </option>
                  {timeSlots.map(slot => (
                    <option 
                      key={slot.time} 
                      value={slot.time}
                      disabled={!slot.available}
                    >
                      {slot.label}
                    </option>
                  ))}
                </TimeSelect>
              </InputWrapper>
              {formData.doctor && formData.date && timeSlots.length > 0 && (
                <TimeSlotInfo>
                  <AlertCircle />
                  Las horas marcadas como "Ocupado" no están disponibles
                </TimeSlotInfo>
              )}
            </FormField>
          </FormSection>

          <FormSection>
            <SectionTitle>Notas Adicionales</SectionTitle>
            <FormField>
              <Label>Notas</Label>
              <TextArea
                name="notes"
                placeholder="Añadir detalles importantes para la cita..."
                value={formData.notes}
                onChange={handleChange}
              />
            </FormField>
          </FormSection>

          <SubmitButton type="submit">
            Confirmar Cita
          </SubmitButton>
        </Form>
      </Content>
    </PageContainer>
  );
};

export default ReservarCita;
